import { FormProvider, useForm } from 'react-hook-form';
import { SubformLivingExpenses } from './SubformLivingExpenses';
import { SubformHouseholdSituation } from './SubformHouseholdSituation';
import { Button, IconNext, styled } from '@payright/web-components';
import SCSpinner from '../../../../components/spinner';
import { ReactNode } from 'react';
import { ButtonCancelPlan } from '../../../../components/buttons/ButtonCancelPlan';

export type FormHouseholdExpensesFormData = {
  householdDetails: {
    appliedPercentage: string;
    incomePercentage: string;
    dependants: string;
    relationshipStatus: string;
  };
  expenseDetails: {
    financialExpensesAmount: string;
    financialExpensesFrequency: string;
    livingExpensesAmount: string;
    livingExpensesFrequency: string;
    loanCount: string;
  };
};

type FormHouseholdExpensesProps = {
  onSubmit: (formData: FormHouseholdExpensesFormData) => void;
  loading: boolean;
  alert: ReactNode;
  relationshipStatus: string | undefined;
  dependants: string | undefined;
  incomePercentage: number | null;
  showLivingExpenses: boolean;
  showFinancialExpenses: boolean;
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 4rem;
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const FormHouseholdExpenses = ({
  onSubmit,
  loading,
  alert,
  relationshipStatus,
  dependants,
  incomePercentage,
  showLivingExpenses,
  showFinancialExpenses,
}: FormHouseholdExpensesProps) => {
  const householdExpensesForm = useForm<FormHouseholdExpensesFormData>({
    defaultValues: {
      householdDetails: {
        dependants: dependants || (null as unknown as string), // we do not let you submit null but it is the starting value
        appliedPercentage: '%',
        incomePercentage: '',
        relationshipStatus: relationshipStatus || (null as unknown as string),
      },
      expenseDetails: {
        financialExpensesAmount: '',
        financialExpensesFrequency: 'Monthly',
        livingExpensesAmount: '',
        livingExpensesFrequency: 'Monthly',
      },
    },
    shouldFocusError: false,
  });

  const { errors } = householdExpensesForm;
  const householdErrors = errors.householdDetails ?? {};
  const expensesErrors = errors.expenseDetails ?? {};

  const { handleSubmit, watch, setValue, unregister } = householdExpensesForm;

  return (
    <FormProvider {...householdExpensesForm}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {alert}
        {showLivingExpenses && (
          <SubformLivingExpenses
            errors={expensesErrors}
            showFinancialExpenses={showFinancialExpenses}
          />
        )}
        <SubformHouseholdSituation
          errors={householdErrors}
          watch={watch}
          setValue={setValue}
          unregister={unregister}
        />
        <Buttons>
          <Button
            withShadow
            className="next-button"
            maxWidth="100%"
            iconPosition="right"
            disabled={loading}
            icon={loading ? <SCSpinner /> : <IconNext />}
          >
            Next Step
          </Button>
          <ButtonCancelPlan disabled={loading} />
        </Buttons>
      </Form>
    </FormProvider>
  );
};
